// Sprint turn-collapse-final-answer — the line that summarises a finished
// turn.
//
// Sprint compact-action-rows (D7, Santi 2026-08-13, option A): this stopped
// being a disclosure. It shipped one day earlier as a chevron that hid the
// whole turn; with actions compacted into counts (`aggregate.ts` D1-D4) a
// finished turn is already short, and two hiding mechanisms on one screen
// is the duplication that confuses. What Santi twice called the valuable
// part — "pone la duración del trabajo" — is exactly what survives: status
// and duration, always visible, hiding nothing.
//
// Chesterton: the disclosure served `founder-first-vision.md` §4
// (aggregation). That principle is now served by the action groups, which
// also work WHILE the agent is working — the window the turn-level collapse
// could never cover, and the window the founder is actually watching.
//
// Sprint turn-verdict-line (D1/D2/D3/D6/D8/D9/D11) — the turn line now also
// says whether the turn's work was VERIFIED (`✓ Tests (47/47)`, `✗ Tests
// falló (3 fallaron)`), replacing the unreachable `TurnSummaryCard`
// (D013-EC-180). `TurnVerification`'s `metric` is an English literal
// produced by `synthesize-summary.ts` (D6 froze that module's shape); this
// component owns the ONLY translation of it into Spanish/English UI copy.
//
// Sprint turn-end-signals-line (D2/D6, TES2) — the line also carries a
// COUNT of actions the agent was blocked from taking this turn
// (`meta.blockedActionCount`), rendered as `BlockedChip` inside the same
// truncating span, BEFORE the verification chips. Always a count, never a
// tool name (§4-D2: a name is jargon and a raw tool_input description is
// English CLI text) — the per-denial detail already ships in developer
// mode via the `result` system pill this field rides on. Muted, never
// `text-status-error` — a blocked action is a fact, not a failed
// verification (D3 amendment above still governs colour).

import { Icon, type IconName } from '../atoms/Icon';
import { useTranslation, type TKey } from '../../i18n';
import type { TurnMeta } from '../../lib/tool-rendering/turn-boundaries';
import type { TurnVerification } from '../../lib/tool-rendering/synthesize-summary';

type Translator = (key: TKey, params?: Record<string, string | number>) => string;

export interface TurnCollapseProps {
  meta: TurnMeta;
  /**
   * Verifications for THIS turn's own close (D2 — never from a borrowed
   * `attributed` id). `undefined` for a turn with no tool calls at all;
   * `[]` for a turn that had tool calls but no verification command
   * (D11a/b) — both render identically to "no verifications" (D8: no
   * empty state).
   */
  verifications?: TurnVerification[];
  /**
   * Sprint work-segment-collapse (PR-B, D4) — pre-formatted, non-null ONLY
   * when the turn has ZERO segment headers (the composition site,
   * `ConversationList.tsx`, is the only place that knows `headerCount`).
   * A turn WITH segment headers passes `null`: the duration moved to the
   * segments that carry it (D3), and this line would otherwise show
   * "Trabajó 2m 9s" twice on a one-segment turn (the "dos mecanismos en la
   * misma pantalla" duplication, Santi 2026-08-13).
   */
  duration?: string | null;
}

export function TurnCollapse({ meta, verifications, duration }: TurnCollapseProps) {
  const { t } = useTranslation();
  // A successful turn says only how long it took — the answer below is the
  // outcome. A turn that failed or was stopped says so in words, because
  // the duration alone would read as success (founder-first §5: name the
  // actor and the state, never make the founder infer it from an icon).
  const statusLabel =
    meta.status === 'error'
      ? t('turnSummary.errored')
      : meta.status === 'stopped'
        ? t('turnSummary.stopped')
        : null;

  // D11a/b: `verifications` is `undefined` for a tool-less turn and `[]`
  // for a turn whose tools never ran a recognized verification command —
  // both must take the "nothing to show" path. Guard on `.length > 0`,
  // never on `!== undefined` (an empty array is truthy).
  const hasVerifications = !!verifications && verifications.length > 0;
  // D2/D6 — render condition widens from `hasVerifications` alone: a
  // turn with zero verifications but a nonzero blocked-action count still
  // has something to say on this line (F-4: the no-empty-state fence
  // (D8) is unaffected — a `0` count still renders nothing here).
  const blockedCount = meta.blockedActionCount;
  const hasBlocked = blockedCount > 0;
  const hasTail = hasVerifications || hasBlocked;
  // D4 (generalises turn-verdict-line D8's no-empty-state rule): the whole
  // line takes the "nothing to say" path when it has nothing to say — a
  // successful turn with segment headers, no verifications and no blocked
  // actions renders NO turn line at all.
  const hasDuration = !!duration;
  if (!statusLabel && !hasTail && !hasDuration) return null;

  return (
    <div
      className="flex w-full items-center gap-1.5 text-caption text-muted-foreground/70"
      data-testid="turn-summary-line"
      data-status={meta.status ?? 'open'}
    >
      {statusLabel && (
        <span className={statusIconClass(meta.status)} data-testid="turn-collapse-status">
          <Icon
            name={statusIconName(meta.status)}
            size={12}
            className="shrink-0"
            aria-hidden="true"
          />
        </span>
      )}
      {/* D9 (introduced, not preserved): the duration never truncates —
          only the verification span (below) does, at narrow viewports.
          Gate 2 B1: `data-testid="turn-summary-line"` is the ROOT div
          above (the whole row), not this span — the visual spec asserts
          on THIS element, not the row, or the assertion is a tautology.
          B1 — the span renders on `hasDuration || statusLabel`, not on
          `hasDuration` alone: an errored/stopped turn almost always HAS
          segment headers (that is the point of D6(d)), so `duration` is
          `null` there and a `hasDuration`-only guard would delete
          "Parado"/"Falló" from the exact turn the founder most needs it
          on. */}
      {(hasDuration || statusLabel) && (
        <span className="text-left shrink-0" data-testid="turn-duration">
          {statusLabel && duration ? `${statusLabel} · ${duration}` : (duration ?? statusLabel)}
        </span>
      )}
      {hasTail && (
        <span
          className="min-w-0 flex-1 truncate"
          data-testid="turn-verification-line"
        >
          {(hasDuration || statusLabel) && ' · '}
          {hasBlocked && (
            <span>
              <BlockedChip count={blockedCount} t={t} />
            </span>
          )}
          {verifications?.map((v, i) => (
            // D11d — the key composite lives on because the two-tests
            // collision it fixed (D-msg-7, `TurnSummaryCard.tsx:72-81`,
            // since deleted) is still reachable here: two `tests`
            // verifications with no `rawCommand` produce identical
            // `kind`s.
            <span key={`${i}-${v.kind}:${v.rawCommand ?? ''}`}>
              {(i > 0 || hasBlocked) && ' · '}
              <VerificationChip v={v} t={t} />
            </span>
          ))}
        </span>
      )}
    </div>
  );
}

function BlockedChip({ count, t }: { count: number; t: Translator }) {
  // D2 — always a count, never a tool name; muted (inherited), never
  // `text-status-error` (D3 amendment: a blocked action is a fact, not a
  // failed verification).
  const label =
    count === 1 ? t('turnSummary.blockedOne') : t('turnSummary.blockedMany', { count });
  return (
    <span className="inline-flex items-center gap-1" data-testid="turn-blocked-actions">
      <Icon name="ShieldAlert" size={12} className="shrink-0" aria-hidden="true" />
      {label}
    </span>
  );
}

function VerificationChip({ v, t }: { v: TurnVerification; t: Translator }) {
  const label = labelFor(v.kind, t);
  // D3 amendment (challenger change 12): colour is on the FAILURE only.
  // A passing check inherits the line's muted tone; painting 2-4 green
  // glyphs would make the non-informative case the loudest thing on the
  // line. A failure is still said in WORDS (`falló`), never left to color
  // alone (founder-first-vision.md §4).
  const iconName: IconName = v.passed ? 'Check' : 'X';
  const colorClass = v.passed ? '' : 'text-status-error';
  // D11c: `metric === null` renders the label with no empty parens.
  const translatedMetric = translateMetric(v.metric, t);
  const metricSuffix = translatedMetric ? ` (${translatedMetric})` : '';
  return (
    <span
      className={`inline-flex items-center gap-1 ${colorClass}`.trim()}
      data-testid={`turn-verification-${v.kind}`}
    >
      <Icon name={iconName} size={12} className="shrink-0" aria-hidden="true" />
      {v.passed ? label : `${label} ${t('turnSummary.failed')}`}
      {metricSuffix}
    </span>
  );
}

/**
 * D3 — `TurnVerification.metric` is an English literal produced by
 * `parseMetric` (`synthesize-summary.ts`, D6-frozen shape): `"3 failed"`,
 * `"120 passed"`, `"12 errors"`, `"5 warnings"`, `"47/47"`. This is the
 * ONLY place that translates it — painting it raw would mix English into
 * a Spanish founder surface (`founder-first-vision.md` §5). Unknown shapes
 * are omitted, never painted raw (D3 table, "cualquier otra").
 *
 * Exported for its own test (single consumer, P6 — no sibling helper
 * file).
 */
export function translateMetric(metric: string | null, t: Translator): string | null {
  if (metric === null) return null;
  if (/^\d+\/\d+$/.test(metric)) return metric; // neutral, as-is

  const failed = /^(\d+)\s+failed$/.exec(metric);
  if (failed) {
    const n = Number(failed[1]);
    return n === 1
      ? t('turnSummary.metricFailedOne')
      : t('turnSummary.metricFailedMany', { count: n });
  }

  const passed = /^(\d+)\s+passed$/.exec(metric);
  if (passed) {
    const n = Number(passed[1]);
    return n === 1
      ? t('turnSummary.metricPassedOne')
      : t('turnSummary.metricPassedMany', { count: n });
  }

  const errors = /^(\d+)\s+errors?$/.exec(metric);
  if (errors) {
    const n = Number(errors[1]);
    return n === 1
      ? t('turnSummary.metricErrorsOne')
      : t('turnSummary.metricErrorsMany', { count: n });
  }

  const warnings = /^(\d+)\s+warnings?$/.exec(metric);
  if (warnings) {
    const n = Number(warnings[1]);
    return n === 1
      ? t('turnSummary.metricWarningsOne')
      : t('turnSummary.metricWarningsMany', { count: n });
  }

  return null;
}

function labelFor(kind: TurnVerification['kind'], t: Translator): string {
  if (kind === 'typecheck') return t('turnSummary.typecheck');
  if (kind === 'tests') return t('turnSummary.tests');
  if (kind === 'lint') return t('turnSummary.lint');
  return t('turnSummary.build');
}

function statusIconName(status: TurnMeta['status']): IconName {
  if (status === 'error') return 'X';
  if (status === 'stopped') return 'Square';
  return 'Check';
}

function statusIconClass(status: TurnMeta['status']): string {
  if (status === 'error') return 'text-status-error';
  return 'text-muted-foreground';
}
